<template>
    <div v-if="activeThread || !contactNotFound || loading || mounting" class="flex flex-col gap-3 md:flex-row">
        <div
            class="bg-white border border-solid border-white flex-1 flex-col relative overflow-hidden md:rounded-2xl md:shadow"
            :class="conversationClasses"
        >
            <div
                class="overflow-hidden flex flex-col grow"
                :style="{
                    paddingBottom: paddingBottom,
                }"
            >
                <div
                    class="bg-linear-to-b from-20% from-white to-white/0 h-12 inset-s-0 inset-e-2 absolute z-10 mdShortHeight:hidden"
                />
                <div
                    class="min-h-14 shrink-0 py-2.5 pr-2.5 pl-3 bg-slate-50/50 backdrop-blur-lg border-solid border border-white flex flex-wrap gap-1 justify-between items-center rounded-2xl absolute inset-bs-2.5 inset-s-2.5 inset-e-2 shadow z-10 mb-3 mdShortHeight:inset-bs-0 mdShortHeight:inset-s-0 mdShortHeight:inset-e-0 mdShortHeight:rounded-none mdShortHeight:border-0 mdShortHeight:border-b"
                >
                    <div class="flex flex-col">
                        <div class="mb-1.5 md:hidden inline-flex">
                            <router-link
                                class="text-sm leading-none!"
                                :to="{
                                    name: 'message-hub',
                                    query: route.query,
                                }"
                            >
                                <font-awesome-icon widthAuto :icon="faArrowLeft"></font-awesome-icon>
                                Threads
                            </router-link>
                        </div>
                        <div v-if="activeThread" class="text-sm font-extrabold leading-none">
                            {{ activeThread.contact_name || formatPhoneNumber(activeThread.contact_phone) }}
                        </div>
                        <div
                            v-else-if="contact.first_name || contact.phone"
                            class="text-sm font-extrabold leading-none"
                        >
                            {{ displayContactName(contact) }}
                        </div>
                        <div v-else class="text-sm font-extrabold leading-none">Loading...</div>
                        <div
                            v-if="
                                activeThread
                                    ? activeThread.contact_phone && activeThread.contact_name
                                    : contact.first_name && contact.phone
                            "
                            class="text-xs leading-tight mt-0.5"
                        >
                            {{ formatPhoneNumber(activeThread ? activeThread.contact_phone : contact.phone) }}
                        </div>
                    </div>

                    <div class="flex items-center gap-2">
                        <vx-button
                            v-if="onCorporate() && canUnsubscribe && contact.phone"
                            @click="unsubscribe"
                            outline
                            size="sm"
                            color="muted"
                        >
                            Unsubscribe
                            <font-awesome-icon widthAuto :icon="faArrowRightFromBracket"></font-awesome-icon>
                        </vx-button>
                        <router-button
                            v-if="route.name !== 'message-hub.contact-view' && !contactNotFound"
                            :to="{
                                name: 'message-hub.contact-view',
                                params: { contactId: contact.id },
                                query: route.query,
                            }"
                            size="sm"
                            color="muted"
                        >
                            Contact Details
                            <font-awesome-icon widthAuto :icon="faCaretRight"></font-awesome-icon>
                        </router-button>
                    </div>
                </div>
                <conversation-messages>
                    <!-- spacer to keep content from getting hidden behind floating contact details -->
                    <div class="pt-32" aria-hidden="true"></div>
                </conversation-messages>
            </div>

            <div
                ref="messageBuilderWrapperRef"
                class="fixed flex flex-col gap-2 inset-s-0 inset-e-0 inset-be-0 shadow-[0px_12px_20px_0px_#000000] bg-white/80 backdrop-blur-sm p-4 md:relative md:inset-s-auto md:inset-e-auto md:inset-be-auto md:shadow-none md:bg-slate-50/90 md:rounded-b-2xl md:rounded-t-none md:border-solid md:border-x-0 md:border-b-0 md:border-t-slate-100 md:border-t mdShortHeight:p-4"
            >
                <conversation-input
                    v-if="FEATURES.simple_message_hub || activeThread?.status !== 'closed' || contactNotFound"
                ></conversation-input>
                <div class="rounded-xl overflow-hidden bg-white border border-solid border-slate-200 flex">
                    <button
                        v-for="action in actions"
                        v-show="action.show"
                        :disabled="!contact.phone"
                        :key="action.label"
                        @click="action.handler"
                        class="appearance-none text-slate-800 bg-transparent flex-1 gap-1 flex flex-col items-center justify-center py-2 px-2 border-r border-solid border-slate-200 border-l-0 border-y-0 hover:bg-slate-50 active:bg-slate-100 last:border-0 mdShortHeight:py-3 mdShortHeight:flex-row"
                        :class="{
                            'opacity-50 cursor-default': action.loading || !contact.phone,
                            'cursor-pointer': !action.loading && contact.phone,
                        }"
                    >
                        <font-awesome-icon
                            widthAuto
                            v-if="!action.loading"
                            :icon="action.icon"
                            :class="[action.color, 'text-sm h-3.5']"
                        ></font-awesome-icon>
                        <vx-icon-loading v-else spin class="w-3.5!"></vx-icon-loading>
                        <span class="text-[10px] leading-none text-slate-500 font-semibold">
                            {{ action.label }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
        <!-- Only show MessageHubContactView when the the contact exists -->
        <router-view v-if="!contactNotFound" class="md:w-[380px] lg:w-[260px] 2xl:w-[424px]"></router-view>
        <message-builder-snippets
            v-model:visible="closeWithSnippetModal"
            @selected="closeWithSnippet($event)"
        ></message-builder-snippets>
        <message-hub-assign-modal v-if="activeThread" v-model:visible="assignAgentModal"></message-hub-assign-modal>
    </div>
    <div v-else>
        <vx-box color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
            <div>Sorry, that content isn't available.</div>
            <router-button :to="{ name: 'message-hub.new-message', query: route.query }" size="lg" type="button">
                New Message
                <font-awesome-icon widthAuto :icon="faCirclePlus"></font-awesome-icon>
            </router-button>
        </vx-box>
    </div>
</template>

<script setup>
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import {
    faArrowRightFromBracket,
    faCaretRight,
    faCircleMinus,
    faCirclePlus,
    faEnvelope,
    faEnvelopeOpen,
    faMessageMinus,
    faUserMinus,
    faUserPen,
    faUserPlus,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxButton, VxIconLoading } from '@voxie/frontend-components';
import { useResizeObserver } from '@vueuse/core';
import { computed, inject, onUnmounted, ref, watch } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { useContactDetails } from '~/components/contacts/contact/useContactDetails';
import { displayContactName, formatPhoneNumber } from '~/components/filters';
import MessageBuilderSnippets from '~/components/general/message-builder/modals/MessageBuilderSnippets.vue';
import RouterButton from '~/components/general/RouterButton.vue';
import ConversationInput from '~/components/messages/browser-extension/components/ConversationInput.vue';
import ConversationMessages from '~/components/messages/browser-extension/components/ConversationMessages.vue';
import { useConversation } from '~/composables/useConversation';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { hasScope } from '~/utils/users';
import MessageHubAssignModal from '~/components/message-hub/MessageHubAssignModal.vue';
import debounce from '~/utils/debounce';

const FEATURES = inject('FEATURES');
const route = useRoute();
const { onCorporate, getCurrentGroupId } = useCurrentGroup();

const {
    contact,
    contactNotFound,
    mount: mountContact,
    unmount: unmountContact,
    existingTransactionalStatus,
    loading,
    canUnsubscribe,
    unsubscribe,
} = useContactDetails();
const {
    mount: mountConversation,
    unmount: unmountConversation,
    mounting,
    activeThread,
    markAsRead,
    markAsUnread,
    togglingReadUnread,
    changeActiveThreadStatus,
    changingThreadStatus,
    closeWithSnippet,
    claimThread,
    unassignThread,
    changingAssignee,
} = useConversation();

watch(
    () => [route.params.contactId, route.query.group],
    // debounce is necessary here for two things:
    // 1. When setting the contact from the MessageHubSidebar -> MessageHubThreadRow
    // 2. When pressing the back and forward button on the browser window
    // 3. When navigating between inboxes in MessageHubNavbar the inbox change reuses a thread if it was already used
    debounce(async ([contactId, groupFromQuery]) => {
        const groupId = groupFromQuery || getCurrentGroupId();
        // only mounts if the contact from the route is different than the composable object
        // MessageHubThreadRow already PREFILLS the contact and conversation on click
        // MessageHubContactView already MOUNTS the contact on route changes
        if (Number(contactId) !== contact.value.id) {
            await mountContact(contactId, groupId);
        }

        mountConversation(undefined, groupId);
    }, 100),
    {
        immediate: true,
    }
);

onUnmounted(() => {
    // clean up, necessary because the global state of the composables are persisted
    // even after the route is no longer accessible, e.g. accessing the new message
    // or going to a different inbox
    unmountContact();
    unmountConversation();
});

const messageBuilderWrapperRef = ref();
const paddingBottom = ref(0);

useResizeObserver(messageBuilderWrapperRef, (elements) => {
    if (window.innerWidth > 768) {
        paddingBottom.value = 0;
        return;
    }

    paddingBottom.value = `${elements[0].target.offsetHeight}px`;
});

const conversationClasses = computed(() => {
    return route.name === 'message-hub.contact-view' ? 'hidden md:flex' : 'flex';
});

const closeWithSnippetModal = ref(false);
const assignAgentModal = ref(false);

const actions = computed(() => {
    if (FEATURES.simple_message_hub) {
        return [
            {
                icon: faEnvelope,
                label: 'Mark Unread',
                show: activeThread.value?.status !== 'open',
                loading: togglingReadUnread.value,
                handler: () => markAsUnread(activeThread.value),
            },
            {
                icon: faEnvelopeOpen,
                label: 'Mark Read',
                show: activeThread.value?.status === 'open',
                loading: togglingReadUnread.value,
                handler: () => markAsRead(activeThread.value),
            },
        ];
    }

    return [
        {
            icon: faCircleMinus,
            label: 'Close',
            color: 'text-rose-500',
            show: activeThread.value?.status === 'open',
            loading: changingThreadStatus.value,
            handler: () => changeActiveThreadStatus(activeThread.value, 'closed'),
        },
        {
            icon: faMessageMinus,
            label: 'Close w/ Snippet',
            show:
                activeThread.value?.status === 'open' &&
                existingTransactionalStatus.value !== 'opt_out' &&
                !contactNotFound.value,
            loading: changingThreadStatus.value,
            handler: () => (closeWithSnippetModal.value = true),
        },
        {
            icon: faCirclePlus,
            label: 'Open',
            show: activeThread.value?.status !== 'open',
            loading: changingThreadStatus.value,
            handler: () => changeActiveThreadStatus(activeThread.value, 'open'),
        },
        {
            icon: faEnvelope,
            label: 'Mark Unread',
            show: !activeThread.value?.unread,
            loading: togglingReadUnread.value,
            handler: () => markAsUnread(activeThread.value),
        },
        {
            icon: faEnvelopeOpen,
            label: 'Mark Read',
            show: activeThread.value?.unread,
            loading: togglingReadUnread.value,
            handler: () => markAsRead(activeThread.value),
        },
        {
            icon: faUserPlus,
            label: 'Claim',
            show:
                hasScope('threads:claim') &&
                !hasScope('threads:assign') &&
                activeThread.value?.assignee_id !== Spark.state.user.id,
            loading: changingAssignee.value,
            handler: () => claimThread(activeThread.value),
        },
        {
            icon: faUserMinus,
            label: 'Unclaim',
            show:
                hasScope('threads:claim') &&
                !hasScope('threads:assign') &&
                activeThread.value?.assignee_id === Spark.state.user.id,
            loading: changingAssignee.value,
            handler: () => unassignThread(activeThread.value),
        },
        {
            icon: faUserPen,
            label: 'Assign',
            show: hasScope('threads:assign'),
            loading: changingAssignee.value,
            handler: () => (assignAgentModal.value = true),
        },
    ];
});
</script>
